import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@apollo/react-hooks';

import useLazyQuery from '../../../../../hooks/useLazyQuery';
import arrowDownPurple from '../../../../../../assets/img/arrow-down-purple.svg';
import close from '../../../../../../assets/img/close-white.svg';
import {
  GET_MEDIA_UPLOAD_SAS,
  GET_COMMUNITIES,
  UPDATE_COMMUNTIES
} from '../../../../../../graphql/Survey';

import Dropdown from '../Conversion/components/Dropdown/Dropdown';

import styles from './Welcome.module.css';

const DropZoneText = ({ isDragActive, uploading }) => {
  if (uploading) {
    return (
      <div className={styles.dropContainer}>
        Uploading image, please wait...
      </div>
    );
  }

  if (isDragActive) {
    return (
      <div className={styles.dropContainer}>Drop the image file here...</div>
    );
  }

  return (
    <div className={styles.dropContainer}>
      <div className={styles.button}>Select a file</div>
      <div className={styles.dropDescription}>or drag and drop file here</div>
    </div>
  );
};

const Welcome = ({
  survey,
  onUpdate,
  setConfirmationPopup,
  surveyRefetch,
  api,
  isAdmin
}) => {
  const generalCommunityName = 'General BUFFL community';
  let defaultCommunities = [];
  if (survey && survey.generalCommunity) {
    defaultCommunities = [generalCommunityName];
  }
  if (survey && survey.communities && survey.communities.length) {
    defaultCommunities = [...defaultCommunities, ...survey.communities];
  }
  const [communities, setCommunities] = useState(defaultCommunities);
  const [surveyName, setSurveyName] = useState(survey.name || '');
  const [surveyNotes, setSurveyNotes] = useState(survey.notes || '');
  const [expanded, setExpanded] = useState(true);
  const [uploading, setUploading] = useState(false);

  const getMediaUploadSAS = useLazyQuery(GET_MEDIA_UPLOAD_SAS);
  const loadCommunitiesQuery = useLazyQuery(GET_COMMUNITIES, {
    fetchPolicy: 'no-cache'
  });
  const [updateCommunities] = useMutation(UPDATE_COMMUNTIES);

  const [selectedLanguage, setSelectedLanguage] = useState(
    (survey && survey.language) || null
  );

  useEffect(
    () => {
      setSurveyName(survey.name || '');
      setSurveyNotes(survey.notes || '');
    },
    [survey]
  );

  const searchCommunities = async (page, search) => {
    const variables = {
      start: 0,
      end: (page + 1) * 10,
      search,
      salt: Math.random().toString()
    };

    const searchResults = await loadCommunitiesQuery({
      variables
    });

    return searchResults &&
      searchResults.data &&
      searchResults.data.getCommunities &&
      searchResults.data.getCommunities.communityTags &&
      searchResults.data.getCommunities.communityTags.length
      ? [
          { label: generalCommunityName, value: generalCommunityName },
          ...searchResults.data.getCommunities.communityTags
            .sort()
            .map(tag => ({
              label: tag,
              value: tag
            }))
        ]
      : [];
  };

  const textareaAutoExpand = field => {
    if (field) {
      const textAreaField = field;

      // Reset field height
      textAreaField.style.height = 'inherit';

      // Get the computed styles for the element
      const computed = window.getComputedStyle(field);

      // Calculate the height
      const height =
        parseInt(computed.getPropertyValue('border-top-width'), 10) +
        parseInt(computed.getPropertyValue('padding-top'), 10) +
        field.scrollHeight +
        parseInt(computed.getPropertyValue('padding-bottom'), 10) +
        parseInt(computed.getPropertyValue('border-bottom-width'), 10);

      textAreaField.style.height = `${height}px`;
    }
  };

  const [questionTextarea, setQuestionTextarea] = useState(null);
  const [notesTextarea, setNotesTextarea] = useState(null);

  const onWindowResize = ev => {
    ev.preventDefault();
    if (questionTextarea) {
      textareaAutoExpand(questionTextarea);
    }

    if (notesTextarea) {
      textareaAutoExpand(notesTextarea);
    }
  };

  const onUpdateSurveyImage = async (filename, origFilename) => {
    await api('updateSurvey', {
      variables: {
        survey: survey.id,
        image: filename,
        imageFilename: origFilename
      }
    });
    await surveyRefetch();
    setUploading(false);
  };

  const onImageSelect = async acceptedFiles => {
    setUploading(true);
    const origFilename = acceptedFiles[0].name;

    const {
      data: { mediaUploadSAS: { uri: sasURI, filename } = {} } = {}
    } = await getMediaUploadSAS({
      variables: {
        fileName: origFilename
      }
    });

    if (!sasURI) {
      // FIXME: error
      return;
    }

    const xhr = new XMLHttpRequest();

    xhr.onload = () => {
      if (xhr.status === 201) {
        onUpdateSurveyImage(filename, origFilename);
      }
    };

    xhr.open('PUT', sasURI, true);
    xhr.setRequestHeader('Content-Type', 'image/jpeg');
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');

    xhr.send(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'image/jpg, image/jpeg, image/png',
    multiple: false,
    onDrop: onImageSelect
  });

  useEffect(
    () => {
      if (questionTextarea) {
        textareaAutoExpand(questionTextarea);
      }

      if (notesTextarea) {
        textareaAutoExpand(notesTextarea);
      }

      window.addEventListener('resize', onWindowResize);

      return () => {
        window.removeEventListener('resize', onWindowResize);
      };
    },
    [questionTextarea]
  );

  const parseDraftQuestion = q => {
    try {
      return JSON.parse(q)
        .blocks.map(draftBlock => draftBlock.text)
        .join(' ');
    } catch (error) {
      return q;
    }
  };

  const getSurveyWordCount = () => {
    const allQuestionsString = survey.questions.reduce(
      (questionString, currentQuestion) =>
        `${questionString}${
          currentQuestion.question
            ? ` ${parseDraftQuestion(currentQuestion.question)}`
            : ''
        }${
          currentQuestion.choices && currentQuestion.choices.length
            ? ` ${currentQuestion.choices.map(choice => choice.answer)}`
            : ''
        }`,
      ''
    );
    return allQuestionsString.trim().length &&
      allQuestionsString.match(/[\wÀ-Üà-øoù-ÿŒœ]+/g)
      ? allQuestionsString.match(/[\wÀ-Üà-øoù-ÿŒœ]+/g).length
      : 0;
  };

  const [surveyWordCount, setSurveyWordCount] = useState(0);

  useEffect(
    () => {
      setSurveyWordCount(getSurveyWordCount());
    },
    [survey.questions]
  );

  const onChangeSurveyLanguage = language => {
    const welcomeBlockConstraintsPopup = localStorage.getItem(
      'qd_cpopup_set_survey_languauge'
    );

    const setLanguage = () => {
      if (language !== selectedLanguage) {
        setSelectedLanguage(language);
        onUpdate('language', language);
      }
    };

    const confirmationPopup = {
      onClick: () => {
        setLanguage();
      },
      onClose: () => setConfirmationPopup(null),
      onDontShowAnymoreChecked: () => {
        if (!welcomeBlockConstraintsPopup) {
          localStorage.setItem('qd_cpopup_set_survey_languauge', true);
        }
      },
      title: 'Survey language',
      description:
        'This setting determines the language of the survey, so it is important to select the native language of (at least some of) your respondents. Once your survey is ready, you can duplicate it in the survey list and launch it in other languages. \n\nThis setting also affects the language of the wizard that guides you through a survey template, to help you prepare the survey directly in the respondent’s language.'
    };

    if (!welcomeBlockConstraintsPopup) {
      setConfirmationPopup(confirmationPopup);
    } else {
      setLanguage();
    }
  };

  const getSelectedCommunities = () => {
    let selectedValue = 'No communities selected';
    if (communities && communities.length) {
      if (communities.length === 1) {
        selectedValue = [...communities[0]];
      } else {
        selectedValue = `${communities.length} communities selected`;
      }
    }
    return selectedValue;
  };

  return (
    <div
      className={styles.container}
      role="presentation"
      /*
      ref={elem => {
        block.reference = elem;
      }}
      */
    >
      <div
        className={styles.titleContainer}
        role="presentation"
        onClick={() => setExpanded(() => setExpanded(!expanded))}
      >
        <div className={styles.title}>Basic Settings</div>
        <img
          src={arrowDownPurple}
          alt="ExpandColapse"
          className={expanded ? styles.arrowExpanded : styles.arrowCollapsed}
        />
      </div>
      {expanded ? (
        <div className={styles.detailsContainer}>
          {isAdmin && (
            <div className={styles.detailContainer}>
              <Dropdown
                selectedValue={getSelectedCommunities()}
                onSelectCompleted={async value => {
                  const newCommunities = [...communities];
                  // Remove from state if exists else add it
                  if (communities.some(community => community === value)) {
                    newCommunities.splice(
                      communities.findIndex(community => community === value),
                      1
                    );
                  } else {
                    newCommunities.push(value);
                  }

                  setCommunities(newCommunities);

                  if (value === generalCommunityName) {
                    // Update generalCommunity property if it was clicked
                    onUpdate('generalCommunity', !survey.generalCommunity);
                  } else {
                    // Update survey communities and do not include generalCommunity
                    const communitiesWithoutGeneral = newCommunities.filter(
                      c => c !== generalCommunityName
                    );
                    await updateCommunities({
                      variables: {
                        survey: survey.id,
                        communities: communitiesWithoutGeneral
                      }
                    });
                  }
                }}
                searchEnabled
                fetchOptions={searchCommunities}
                loading={false}
                findList={communities}
                addNew={false}
                customBackgroundStyle={{ backgroundPosition: '98.5% center' }}
              />
            </div>
          )}
          <div className={styles.detailContainer}>
            <textarea
              className={styles.surveyName}
              rows="1"
              ref={element => {
                if (element) {
                  setQuestionTextarea(element);
                }
              }}
              placeholder="Type Survey Name here.."
              onBlur={() => onUpdate('name', surveyName)}
              onChange={e => {
                textareaAutoExpand(questionTextarea);
                setSurveyName(e.target.value);
              }}
              maxLength="1000"
              value={surveyName}
            />
          </div>
          <div className={styles.detailContainer}>
            <select
              className={
                selectedLanguage
                  ? styles.dropdown
                  : `${styles.dropdown} ${styles.dropdownPlaceholder}`
              }
              onChange={e => onChangeSurveyLanguage(e.target.value)}
              value={selectedLanguage}
            >
              <option value={null} disabled selected>
                Survey language
              </option>
              <option value="Dutch">Dutch</option>
              <option value="French">French</option>
              <option value="German">German</option>
              <option value="English">English</option>
            </select>
          </div>
          <div className={styles.detailContainer}>
            <textarea
              className={
                !surveyNotes || surveyNotes === ''
                  ? styles.description
                  : `${styles.description} ${styles.notesField}`
              }
              rows="1"
              ref={element => {
                if (element) {
                  setNotesTextarea(element);
                }
              }}
              placeholder="Project notes (not visible for respondents)"
              onBlur={() => onUpdate('notes', surveyNotes)}
              onChange={e => {
                textareaAutoExpand(notesTextarea);
                setSurveyNotes(e.target.value);
              }}
              maxLength="2000"
              value={surveyNotes}
            />
          </div>
          <div className={styles.wordCount}>
            Total survey word count: {surveyWordCount}
          </div>

          <div className={styles.detailContainer}>
            <div className={styles.dropContainerTitle}>
              Upload a cover picture
            </div>
            {survey.image ? (
              <div className={styles.imageContainer}>
                <img
                  className={styles.coverImage}
                  alt="Cover"
                  src={survey.image}
                />
                <div
                  className={styles.removeImageContainer}
                  role="presentation"
                  onClick={() => onUpdateSurveyImage(null, null)}
                >
                  <img alt="Close" src={close} />
                </div>
              </div>
            ) : (
              <div className={styles.dropZone}>
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <DropZoneText
                    isDragActive={isDragActive}
                    pdfCoupons={[]}
                    uploading={uploading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Welcome;
